import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Plus, Edit, Trash2, Download, X, Link as LinkIcon } from 'lucide-react';
import { assemblyNotesAPI } from '../../services/api';
import { sanitizeHtml } from '../../utils/sanitize';
import { toast } from 'sonner';

const AssemblyTab = ({ 
  assemblyNotes, 
  setAssemblyNotes,
  isAdmin 
}) => {
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    title: '',
    date: '',
    content: '',
    pdf_url: ''
  });

  const resetNoteForm = () => {
    setNoteForm({ title: '', date: '', content: '', pdf_url: '' });
    setEditingNote(null);
  };

  const handleRemovePdf = () => {
    setNoteForm(prev => ({ ...prev, pdf_url: '' }));
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const newNote = await assemblyNotesAPI.create(noteForm);
      setAssemblyNotes([newNote, ...assemblyNotes]);
      setIsNoteDialogOpen(false);
      resetNoteForm();
      toast.success('Compte-rendu créé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      const updated = await assemblyNotesAPI.update(editingNote.note_id, noteForm);
      setAssemblyNotes(assemblyNotes.map(n => n.note_id === editingNote.note_id ? updated : n));
      setIsNoteDialogOpen(false);
      resetNoteForm();
      toast.success('Compte-rendu mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte-rendu ?')) return;
    try {
      await assemblyNotesAPI.delete(noteId);
      setAssemblyNotes(assemblyNotes.filter(n => n.note_id !== noteId));
      toast.success('Compte-rendu supprimé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title,
      date: note.date,
      content: note.content || '',
      pdf_url: note.pdf_url || ''
    });
    setIsNoteDialogOpen(true);
  };

  return (
    <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-purple-300">Registre Officiel</h2>
        
        {isAdmin() && (
          <Dialog open={isNoteDialogOpen} onOpenChange={(open) => {
            setIsNoteDialogOpen(open);
            if (!open) resetNoteForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
                <Plus className="mr-2 h-5 w-5" />
                Nouveau CR
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-purple-300">
                  {editingNote ? 'Modifier le compte-rendu' : 'Nouveau compte-rendu'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={editingNote ? handleUpdateNote : handleCreateNote} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-purple-300">Titre</Label>
                  <Input
                    value={noteForm.title}
                    onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                    placeholder="Ex: AG du 15 janvier 2026"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-purple-300">Date</Label>
                  <Input
                    type="date"
                    value={noteForm.date}
                    onChange={(e) => setNoteForm({ ...noteForm, date: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                    required
                  />
                </div>
                
                {/* PDF URL */}
                <div className="space-y-2">
                  <Label className="text-purple-300">Lien du document PDF (optionnel)</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        value={noteForm.pdf_url}
                        onChange={(e) => setNoteForm({ ...noteForm, pdf_url: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white pl-10"
                        placeholder="https://drive.google.com/... ou autre lien PDF"
                      />
                    </div>
                    {noteForm.pdf_url && (
                      <Button
                        type="button"
                        onClick={handleRemovePdf}
                        variant="destructive"
                        size="icon"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs">Hébergez votre PDF sur Google Drive, Dropbox, etc. et collez le lien ici</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-purple-300">Contenu</Label>
                  <RichTextEditor
                    value={noteForm.content}
                    onChange={(value) => setNoteForm({ ...noteForm, content: value })}
                    placeholder="Rédigez le compte-rendu de l'AG..."
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                  {editingNote ? 'Modifier' : 'Créer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {assemblyNotes.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          Aucun compte-rendu disponible
        </div>
      ) : (
        <div className="space-y-6">
          {assemblyNotes.map((note) => (
            <div key={note.note_id} className="p-4 sm:p-6 bg-black/30 rounded-lg border border-purple-500/20">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-purple-300">{note.title}</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Date : {new Date(note.date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {note.pdf_url && (
                    <a
                      href={note.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white text-sm transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      <span className="hidden sm:inline">Télécharger PDF</span>
                      <span className="sm:hidden">PDF</span>
                    </a>
                  )}
                  {isAdmin() && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openEditNote(note)}
                        className="border-purple-500/30 text-purple-300 hover:bg-purple-900/30"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDeleteNote(note.note_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {note.content && (
                <div 
                  className="prose prose-invert prose-purple max-w-none text-gray-300 text-sm sm:text-base"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.content) }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default AssemblyTab;
